import { graphql } from 'gatsby';
import YouTube from 'react-youtube';
import { ButtonLink } from '~components/ButtonLink';
import { MixedMedia } from '~components/MixedMedia';
import { rawContentToReactComponents } from '~utils/rawContentToReactComponents';
import * as styles from './ContentfulSharedImageText.module.css';

import type { Options } from '@contentful/rich-text-react-renderer';
import type { ImageType } from '~components/Image';
import type { Layout, Theme } from '~models/contentful';

type ComponentSharedImageTextProps = {
  className?: string;
  imageClassName?: string;
  textClassName?: string;
  content: ContentfulComponentSharedImageText;
  renderProps?: Options;
  showCtaButton?: boolean;
  enableYouTubePlayer?: boolean;
};

export type ContentfulComponentSharedImageText = {
  __typename: 'ContentfulComponentSharedImageText';
  contentful_id: string;
  name: string;
  text: {
    raw: string;
  };
  image: ImageType;
  youTubeVideoId?: string;
  reverseOrder?: boolean;
  callToActionLabel?: string;
  callToActionTarget?: string;
  theme?: Theme;
  blockLayout?: Layout;
};

export const ComponentSharedImageText: React.FC<ComponentSharedImageTextProps> = ({
  renderProps = {},
  showCtaButton = false,
  enableYouTubePlayer = false,
  ...props
}): JSX.Element => {
  const { callToActionLabel, callToActionTarget } = props.content;
  return (
    <div className={props.className}>
      {enableYouTubePlayer && props.content.youTubeVideoId ? (
        <YouTube videoId={props.content.youTubeVideoId} iframeClassName={styles.youTubeIFrame} />
      ) : (
        <MixedMedia className={props.imageClassName} media={props.content.image} />
      )}
      <section className={props.textClassName} style={{ whiteSpace: 'pre-wrap' }}>
        {rawContentToReactComponents(props.content.text.raw, renderProps)}
        {!!(showCtaButton && callToActionLabel && callToActionTarget) && (
          <ButtonLink className={styles.button} href={callToActionTarget} outline>
            {callToActionLabel}
          </ButtonLink>
        )}
      </section>
    </div>
  );
};

export const query = graphql`
  fragment ComponentSharedImageText on ContentfulComponentSharedImageText {
    __typename
    contentful_id
    name
    text {
      raw
    }
    image {
      ...MixedMedia
    }
    youTubeVideoId
    reverseOrder
    callToActionLabel
    callToActionTarget
    theme
    blockLayout
  }
`;
